import { useState, useEffect } from "react"
import styled from "styled-components"
import { X, Image, Hash, Send, Loader } from "lucide-react"
import { useTheme } from "../context/ThemeContext"

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`

const ModalContainer = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadow};
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const ModalBody = styled.div`
  padding: 20px;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  resize: vertical;
  font-size: 14px;
  font-family: inherit;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const ImagePreviewContainer = styled.div`
  margin-top: 16px;
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
`

const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${props => props.theme.borderLight};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`

const HashtagSection = styled.div`
  margin-top: 16px;
`

const HashtagInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 14px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const HashtagDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`

const HashtagTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid ${props => props.theme.border};
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const FileInput = styled.input`
  display: none;
`

export default function EditPostModal({ isOpen, onClose, post, onPostUpdated }) {
  const { theme } = useTheme()
  const [content, setContent] = useState("")
  const [hashtags, setHashtags] = useState([])
  const [hashtagInput, setHashtagInput] = useState("")
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)

  // 모달이 열릴 때 기존 게시물 데이터로 초기화
  useEffect(() => {
    if (isOpen && post) {
      setContent(post.content || post.description || "")
      setHashtags(post.hashtags || post.tags || [])
      setImages(post.images || [])
    }
  }, [isOpen, post])

  const handleClose = () => {
    setContent("")
    setHashtags([])
    setHashtagInput("")
    setImages([])
    onClose()
  }

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    
    files.forEach(file => {
      const reader = new FileReader()
      reader.onload = (event) => {
        const newImage = {
          file,
          url: event.target.result,
          isNew: true
        }
        setImages(prev => [...prev, newImage])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  const addHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags(prev => [...prev, hashtagInput.trim()])
      setHashtagInput("")
    }
  }

  const removeHashtag = (tagToRemove) => {
    setHashtags(prev => prev.filter(tag => tag !== tagToRemove))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      addHashtag()
    }
  }

  const handleSubmit = async () => {
    if (!content.trim()) {
      alert("게시물 내용을 입력해주세요.")
      return
    }

    setLoading(true)
    
    try {
      const newImages = images.filter(img => img.isNew)
      const existingImages = images.filter(img => !img.isNew)

      // 새 이미지를 file.skynet.re.kr로 직접 업로드 (백엔드 /post/upload-images 부재)
      let uploadedImages = []
      if (newImages.length > 0) {
        const formData = new FormData()
        newImages.forEach((img) => formData.append('files', img.file))

        const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
          method: 'POST',
          body: formData
        })

        const uploadResult = await uploadResponse.json()
        if (uploadResult?.files?.length) {
          uploadedImages = uploadResult.files.map(f => ({ url: f.url }))
        } else {
          alert('이미지 업로드에 실패했습니다.')
          setLoading(false)
          return
        }
      }
      
      // 게시물 업데이트
      const updateData = {
        content: content.trim(),
        hashtags,
        images: [...existingImages, ...uploadedImages]
      }
      
      const response = await fetch(`/post/update/${post.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })
      
      const result = await response.json()
      
      if (result.success) {
        alert("게시물이 수정되었습니다.")
        onPostUpdated && onPostUpdated()
        handleClose()
      } else {
        alert(result.message || "게시물 수정에 실패했습니다.")
      }
    } catch (error) {
      console.error("게시물 수정 오류:", error)
      alert("게시물 수정 중 오류가 발생했습니다.")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>게시물 편집</ModalTitle>
          <CloseButton theme={theme} onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <TextArea
            theme={theme}
            placeholder="수정할 내용을 입력하세요..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          
          {images.length > 0 && (
            <ImagePreviewContainer>
              <ImageGrid>
                {images.map((image, index) => (
                  <ImagePreview key={index} theme={theme}>
                    <img src={image.url} alt={`Preview ${index + 1}`} />
                    <RemoveImageButton onClick={() => removeImage(index)}>
                      <X size={12} />
                    </RemoveImageButton>
                  </ImagePreview>
                ))}
              </ImageGrid>
            </ImagePreviewContainer>
          )}
          
          <HashtagSection>
            <HashtagInput
              theme={theme}
              placeholder="해시태그를 입력하고 엔터나 스페이스를 눌러주세요"
              value={hashtagInput}
              onChange={(e) => setHashtagInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {hashtags.length > 0 && (
              <HashtagDisplay>
                {hashtags.map((tag, index) => (
                  <HashtagTag
                    key={index}
                    theme={theme}
                    onClick={() => removeHashtag(tag)}
                  >
                    <Hash size={10} />
                    {tag}
                    <X size={10} />
                  </HashtagTag>
                ))}
              </HashtagDisplay>
            )}
          </HashtagSection>
        </ModalBody>
        
        <ModalFooter theme={theme}>
          <div>
            <ActionButton theme={theme} as="label">
              <Image size={16} />
              이미지 추가
              <FileInput
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </ActionButton>
          </div>
          
          <SaveButton theme={theme} onClick={handleSubmit} disabled={loading || !content.trim()}>
            {loading ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            {loading ? "저장 중..." : "저장"}
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}